import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NavigationEnd, Router, RouterLink } from '@angular/router'
import { DEV_OR_LOCALHOST, MAP } from '@bases/base.token'
import { SessionManagerService } from '@core/services/session-manager.service'
import { environment } from '@env/environment'
import { buildDate, commitHash, version } from '@env/version'
import { KEYCLOAK_EVENT_SIGNAL } from 'keycloak-angular'
import * as L from 'leaflet'
import { MatomoTracker } from 'ngx-matomo-client'
import { BehaviorSubject, filter, map } from 'rxjs'
import { DialogFeedbackComponent } from './dialog-feedback/dialog-feedback.component'
import { DialogUserManagementComponent } from './dialog-user-management/dialog-user-management.component'
import { SearchBarComponent } from './searchbar/searchbar.component'

@Component({
  selector: 'app-header-bar',
  templateUrl: './header-bar.component.html',
  styleUrl: './header-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    // Component
    SearchBarComponent,

    // Material
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class HeaderBarComponent {
  devOrLocalhost = inject(DEV_OR_LOCALHOST) as BehaviorSubject<string>
  private readonly _map = inject(MAP) as BehaviorSubject<L.DrawMap>
  private readonly _tracker = inject(MatomoTracker)
  readonly dialog = inject(MatDialog)
  readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL)
  readonly sessionManagerService = inject(SessionManagerService)

  isLoggedIn$ = this.sessionManagerService.isLoggedIn$
  hasIntegrationAdminAttribute$ = this.sessionManagerService.hasIntegrationAdminAttribute$

  version = version
  versionInfo = `${commitHash} (${buildDate})`

  logoUrl = environment.logoUrl
  websiteUrl = environment.websiteUrl
  wikiUrl = environment.wikiUrl
  feedback = environment.feedback
  feedbackUrl = environment.feedbackUrl

  currentUrl$ = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).urlAfterRedirects.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  )

  showIntegrationButton = computed(
    () =>
      this.currentUrl$() === '/map' && this.isLoggedIn$() && this.hasIntegrationAdminAttribute$(),
  )

  showMapButton = computed(() => this.currentUrl$() !== '/map')

  constructor(public router: Router) {}

  recenterMap() {
    this._map
      .getValue()
      .setView(L.latLng(environment.centerMap[0], environment.centerMap[1]), environment.zoom)
  }

  openFeedback() {
    this.dialog.open(DialogFeedbackComponent, { disableClose: true })
  }

  openUserManagement() {
    this.dialog.open(DialogUserManagementComponent)
  }

  login() {
    // Tracks login click with Matomo
    this._tracker.trackEvent('Login', 'Login Button Click', 'user try to login or register')
    this.sessionManagerService.login()
  }
}
